import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, haceDiasISO, agruparPorMes } from '../lib/format';
import { q, crearMovimiento, borrarMovimientoSimple } from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import { ivaDesdeTotal } from '../lib/iva';
import ErrorBanner from './ErrorBanner';

const UNIDADES = ['unidad', 'kg', 'g', 'L', 'mL', 'pack', 'caja', 'bolsa', 'par'];

const itemVacio = () => ({ nombre: '', unidad: 'unidad', cantidad: 1, precio: '', marca: '' });
const compraVacia = () => ({ tienda: '', fecha: hoyISO(), bolsillo_id: '', notas: '', con_factura: false });
const listaVacia = { nombre: '', marca: '', cantidad: 1, unidad: 'unidad', precio_estimado: '' };

function Compras({ userId }) {
  const [tab, setTab] = useState('historial');
  const [compras, setCompras] = useState([]);
  const [items, setItems] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [listaItems, setListaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [mesesAbiertos, setMesesAbiertos] = useState(null); // null = solo el mes más reciente
  const { saving, error, setError, run } = useAccion();

  const [compraForm, setCompraForm] = useState(compraVacia);
  const [itemsForm, setItemsForm] = useState([itemVacio()]);

  const [listaForm, setListaForm] = useState(listaVacia);
  const [editingListaId, setEditingListaId] = useState(null);
  const [editListaForm, setEditListaForm] = useState({});

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [c, i, b, li] = await Promise.all([
      q(supabase.from('compras').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
      q(supabase.from('items_compra').select('*').eq('user_id', userId)),
      q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('lista_compras').select('*').eq('user_id', userId).order('created_at')),
    ]);
    setCompras(c || []);
    setItems(i || []);
    setBolsillos(b || []);
    setListaItems(li || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  // --- Items del formulario ---
  const handleAddItem = () => setItemsForm(p => [...p, itemVacio()]);
  const handleRemoveItem = (idx) => setItemsForm(p => p.filter((_, i) => i !== idx));
  const handleItemChange = (idx, field, value) =>
    setItemsForm(p => p.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  const totalForm = itemsForm.reduce(
    (s, i) => s + (parseFloat(i.precio) || 0) * (parseFloat(i.cantidad) || 1), 0
  );

  // --- Guardar compra ---
  const handleGuardarCompra = async (e) => {
    e.preventDefault();
    const itemsValidos = itemsForm.filter(i => i.nombre.trim());
    if (itemsValidos.length === 0) { setError('Agrega al menos un producto.'); return; }

    const ok = await run(async () => {
      const compra = await q(supabase.from('compras').insert({
        user_id: userId,
        fecha: compraForm.fecha,
        tienda: compraForm.tienda || null,
        total: totalForm,
        bolsillo_id: compraForm.bolsillo_id || null,
        notas: compraForm.notas || null,
        con_factura: compraForm.con_factura,
        iva: compraForm.con_factura ? ivaDesdeTotal(totalForm) : 0,
      }).select().single());

      try {
        await q(supabase.from('items_compra').insert(
          itemsValidos.map(i => ({
            user_id: userId,
            compra_id: compra.id,
            nombre: i.nombre.trim(),
            unidad: i.unidad || 'unidad',
            cantidad: parseFloat(i.cantidad) || 1,
            precio: parseFloat(i.precio) || 0,
            marca: i.marca?.trim() || null,
          }))
        ));
      } catch (err) {
        // Sin productos la compra queda incompleta: se deshace
        await supabase.from('compras').delete().eq('id', compra.id);
        throw err;
      }

      if (compraForm.bolsillo_id && totalForm > 0) {
        const mov = await crearMovimiento({
          userId,
          bolsilloId: compraForm.bolsillo_id,
          tipo: 'egreso',
          monto: totalForm,
          descripcion: `Compra${compraForm.tienda ? ` en ${compraForm.tienda}` : ''}`,
          fecha: compraForm.fecha,
          rubro: 'hogar',
          conFactura: compraForm.con_factura,
          iva: ivaDesdeTotal(totalForm),
        });
        await q(supabase.from('compras').update({ movimiento_id: mov.id }).eq('id', compra.id));
      }
    }, loadData);

    if (ok) {
      setItemsForm([itemVacio()]);
      setCompraForm(compraVacia());
      setTab('historial');
    }
  };

  const handleDeleteCompra = async (compra) => {
    const msg = compra.movimiento_id
      ? '¿Eliminar esta compra y todos sus productos?\n\nTambién se eliminará la salida de dinero de la categoría, así que el saldo vuelve a como estaba.'
      : '¿Eliminar esta compra y todos sus productos?';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('compras').delete().eq('id', compra.id));
      await borrarMovimientoSimple(compra.movimiento_id);
    }, loadData);
  };

  // --- Lista de compras ---
  const handleAddLista = async (e) => {
    e.preventDefault();
    if (!listaForm.nombre.trim()) return;
    const ok = await run(() => q(supabase.from('lista_compras').insert({
      user_id: userId,
      nombre: listaForm.nombre.trim(),
      marca: listaForm.marca.trim() || null,
      cantidad: parseFloat(listaForm.cantidad) || 1,
      unidad: listaForm.unidad,
      precio_estimado: parseFloat(listaForm.precio_estimado) || 0,
      comprado: false,
    })), loadData);
    if (ok) setListaForm(listaVacia);
  };

  const handleToggleLista = (item) =>
    run(() => q(supabase.from('lista_compras').update({ comprado: !item.comprado }).eq('id', item.id)), loadData);

  const handleDeleteLista = (id) =>
    run(() => q(supabase.from('lista_compras').delete().eq('id', id)), loadData);

  const handleStartEditLista = (item) => {
    setEditingListaId(item.id);
    setEditListaForm({
      nombre: item.nombre,
      marca: item.marca || '',
      cantidad: item.cantidad,
      unidad: item.unidad || 'unidad',
      precio_estimado: item.precio_estimado || '',
    });
  };

  const handleSaveEditLista = async (id) => {
    if (!editListaForm.nombre.trim()) return;
    const ok = await run(() => q(supabase.from('lista_compras').update({
      nombre: editListaForm.nombre.trim(),
      marca: editListaForm.marca.trim() || null,
      cantidad: parseFloat(editListaForm.cantidad) || 1,
      unidad: editListaForm.unidad,
      precio_estimado: parseFloat(editListaForm.precio_estimado) || 0,
    }).eq('id', id)), loadData);
    if (ok) setEditingListaId(null);
  };

  const handleClearComprados = async () => {
    const ids = listaItems.filter(i => i.comprado).map(i => i.id);
    if (!ids.length) return;
    await run(() => q(supabase.from('lista_compras').delete().in('id', ids)), loadData);
  };

  const listaPendientes = listaItems.filter(i => !i.comprado);
  const listaComprados = listaItems.filter(i => i.comprado);
  const totalListaEstimado = listaPendientes.reduce(
    (s, i) => s + (parseFloat(i.precio_estimado) || 0) * (parseFloat(i.cantidad) || 1), 0
  );

  // --- Predicción: productos que aparecen en 2+ compras de las últimas 4 semanas ---
  const getPrediccion = () => {
    const desde = haceDiasISO(28);
    const idsRecientes = new Set(compras.filter(c => c.fecha >= desde).map(c => c.id));
    const agrupado = {};
    items.filter(i => idsRecientes.has(i.compra_id)).forEach(item => {
      const key = item.nombre.toLowerCase().trim();
      if (!agrupado[key]) agrupado[key] = { nombre: item.nombre, precios: [], comprasSet: new Set() };
      agrupado[key].precios.push((item.precio || 0) * (item.cantidad || 1));
      agrupado[key].comprasSet.add(item.compra_id);
    });
    return Object.values(agrupado)
      .filter(g => g.comprasSet.size >= 2)
      .map(g => ({
        nombre: g.nombre,
        precioPromedio: Math.round(g.precios.reduce((s, p) => s + p, 0) / g.precios.length),
        veces: g.comprasSet.size,
      }))
      .sort((a, b) => b.veces - a.veces);
  };

  const prediccion = getPrediccion();
  const totalPrediccion = prediccion.reduce((s, i) => s + i.precioPromedio, 0);
  const gastoTotal = compras.reduce((s, c) => s + (c.total || 0), 0);

  // --- Historial agrupado por mes ---
  const gruposCompras = agruparPorMes(compras);
  const mesAbierto = (clave, idx) => (mesesAbiertos ? mesesAbiertos.has(clave) : idx === 0);
  const toggleMes = (clave) => setMesesAbiertos(prev => {
    const next = new Set(prev || (gruposCompras.length ? [gruposCompras[0].clave] : []));
    if (next.has(clave)) next.delete(clave); else next.add(clave);
    return next;
  });

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const tabStyle = (key) => ({
    padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer',
    fontWeight: tab === key ? 700 : 400,
    color: tab === key ? 'var(--primary)' : 'var(--text-secondary)',
    borderBottom: tab === key ? '2px solid var(--primary)' : '2px solid transparent',
    marginBottom: -2, transition: 'all 0.15s', fontSize: '0.85rem', whiteSpace: 'nowrap',
  });

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Mis Compras</h2>
          <p className="page-subtitle">Registra compras, lleva el historial y predice gastos</p>
        </div>
        <button className="btn-primary" onClick={() => setTab('nueva')}>+ Nueva compra</button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)', overflowX: 'auto' }}>
        <button style={tabStyle('historial')} onClick={() => setTab('historial')}>🧾 Historial</button>
        <button style={tabStyle('nueva')} onClick={() => setTab('nueva')}>+ Nueva compra</button>
        <button style={tabStyle('lista')} onClick={() => setTab('lista')}>
          📋 Lista{listaPendientes.length > 0 ? ` (${listaPendientes.length})` : ''}
        </button>
        <button style={tabStyle('prediccion')} onClick={() => setTab('prediccion')}>🔮 Predicción</button>
      </div>

      {/* ─── HISTORIAL ─── */}
      {tab === 'historial' && (
        <>
          {compras.length > 0 && (
            <div className="summary-cards" style={{ marginBottom: 24 }}>
              <div className="card">
                <div className="card-icon">🧾</div>
                <div className="card-content"><h4>Compras registradas</h4><p className="card-amount neutral">{compras.length}</p></div>
              </div>
              <div className="card">
                <div className="card-icon">💸</div>
                <div className="card-content"><h4>Gasto total</h4><p className="card-amount negative">{formatCLP(gastoTotal)}</p></div>
              </div>
              {compras[0] && (
                <div className="card">
                  <div className="card-icon">📅</div>
                  <div className="card-content">
                    <h4>Última compra</h4>
                    <p className="card-amount neutral" style={{ fontSize: '1rem' }}>{formatFecha(compras[0].fecha)}</p>
                    {compras[0].tienda && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{compras[0].tienda}</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {compras.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>🧾</div>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Sin compras registradas</p>
              <p style={{ fontSize: '0.9rem' }}>Registra tu primera compra para llevar el control y predecir gastos futuros.</p>
              <button className="btn-primary" onClick={() => setTab('nueva')} style={{ marginTop: 16 }}>+ Registrar compra</button>
            </div>
          ) : (
            <div className="meses-list">
              {gruposCompras.map((g, idx) => {
                const totalMes = g.registros.reduce((s, c) => s + Number(c.total || 0), 0);
                const abierto = mesAbierto(g.clave, idx);
                return (
                  <div key={g.clave} className="mes-grupo">
                    <button type="button" className="mes-header" onClick={() => toggleMes(g.clave)}>
                      <span className="mes-chevron">{abierto ? '▼' : '▶'}</span>
                      <span className="mes-nombre">
                        {g.nombre}
                        <span className="mes-count">{g.registros.length} compra{g.registros.length !== 1 ? 's' : ''}</span>
                      </span>
                      <span className="mes-totales"><strong className="text-danger">{formatCLP(totalMes)}</strong></span>
                    </button>
                    {abierto && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
                        {g.registros.map(compra => {
                          const compraItems = items.filter(i => i.compra_id === compra.id);
                          const isExpanded = expandedId === compra.id;
                          return (
                            <div key={compra.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                              <div
                                style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                                onClick={() => setExpandedId(isExpanded ? null : compra.id)}
                              >
                                <span style={{ fontSize: '1.5rem' }}>🧾</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <div>
                                      <p style={{ fontWeight: 700, margin: 0, fontSize: '0.95rem' }}>{compra.tienda || 'Compra sin nombre'}</p>
                                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                        {formatFecha(compra.fecha)} · {compraItems.length} producto{compraItems.length !== 1 ? 's' : ''}
                                      </p>
                                    </div>
                                    <p style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: '#ef4444', flexShrink: 0 }}>{formatCLP(compra.total)}</p>
                                  </div>
                                </div>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                              </div>

                              {isExpanded && (
                                <div style={{ borderTop: '1px solid var(--border)', padding: '12px 18px', background: 'var(--bg)' }}>
                                  {compraItems.length > 0 && (
                                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 340 }}>
                                        <thead>
                                          <tr style={{ color: 'var(--text-muted)', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                            <th style={{ padding: '4px 0', fontWeight: 600 }}>Producto</th>
                                            <th style={{ padding: '4px 8px' }}>Marca</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>Cant.</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>Unidad</th>
                                            <th style={{ padding: '4px 0', textAlign: 'right' }}>Precio</th>
                                            <th style={{ padding: '4px 0', textAlign: 'right' }}>Subtotal</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {compraItems.map(item => (
                                            <tr key={item.id} style={{ borderTop: '1px solid var(--border)' }}>
                                              <td style={{ padding: '7px 0', fontWeight: 500 }}>{item.nombre}</td>
                                              <td style={{ padding: '7px 8px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{item.marca || '—'}</td>
                                              <td style={{ padding: '7px 6px', textAlign: 'center' }}>{item.cantidad}</td>
                                              <td style={{ padding: '7px 6px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{item.unidad || 'unidad'}</td>
                                              <td style={{ padding: '7px 0', textAlign: 'right' }}>{formatCLP(item.precio)}</td>
                                              <td style={{ padding: '7px 0', textAlign: 'right', fontWeight: 600 }}>{formatCLP(item.precio * (item.cantidad || 1))}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {compra.notas && (
                                    <p style={{ margin: '10px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>📝 {compra.notas}</p>
                                  )}
                                  <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                      className="btn-secondary"
                                      style={{ fontSize: '0.78rem', padding: '4px 10px', color: 'var(--danger)' }}
                                      onClick={() => handleDeleteCompra(compra)}
                                      disabled={saving}
                                    >× Eliminar</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ─── NUEVA COMPRA ─── */}
      {tab === 'nueva' && (
        <form onSubmit={handleGuardarCompra}>
          {/* Datos de la compra */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 14 }}>1. Datos de la compra</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label>Local / Tienda</label>
                <input
                  type="text"
                  value={compraForm.tienda}
                  onChange={e => setCompraForm(f => ({ ...f, tienda: e.target.value }))}
                  placeholder="Ej: Líder, Jumbo..."
                />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input
                  type="date"
                  value={compraForm.fecha}
                  onChange={e => setCompraForm(f => ({ ...f, fecha: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Descontar de categoría <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
                <select
                  value={compraForm.bolsillo_id}
                  onChange={e => setCompraForm(f => ({ ...f, bolsillo_id: e.target.value }))}
                >
                  <option value="">No descontar</option>
                  {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
                {compraForm.bolsillo_id && (
                  <span className="field-hint">Se registrará un egreso automáticamente</span>
                )}
              </div>
              <div className="form-group">
                <label>¿Con factura?</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                  <input type="checkbox" checked={compraForm.con_factura}
                    onChange={e => setCompraForm(f => ({ ...f, con_factura: e.target.checked }))}
                    style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                  Sí, suma al IVA crédito
                </label>
                {compraForm.con_factura && totalForm > 0 && (
                  <span className="field-hint">IVA de esta compra: {formatCLP(ivaDesdeTotal(totalForm))}</span>
                )}
              </div>
              <div className="form-group">
                <label>Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
                <input
                  type="text"
                  value={compraForm.notas}
                  onChange={e => setCompraForm(f => ({ ...f, notas: e.target.value }))}
                  placeholder="Ej: Compra semanal..."
                />
              </div>
            </div>
          </div>

          {/* Productos */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>2. Productos ({itemsForm.length})</h3>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddItem}
                style={{ fontSize: '0.85rem', padding: '5px 14px' }}
              >+ Agregar</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {itemsForm.map((item, idx) => (
                <div key={idx} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)' }}>
                  {/* Fila 1: nombre + marca */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Nombre del producto *</label>
                      <input
                        type="text"
                        value={item.nombre}
                        onChange={e => handleItemChange(idx, 'nombre', e.target.value)}
                        placeholder="Ej: Leche, Pan, Arroz..."
                        style={{ fontSize: '0.9rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Marca</label>
                      <input
                        type="text"
                        value={item.marca}
                        onChange={e => handleItemChange(idx, 'marca', e.target.value)}
                        placeholder="Ej: Soprole, Carozzi..."
                        style={{ fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>
                  {/* Fila 2: cantidad + unidad + precio + eliminar */}
                  <div className="compra-item-grid" style={{ display: 'grid', gridTemplateColumns: '80px 1fr 120px auto', gap: 10, alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Cantidad</label>
                      <input
                        type="number"
                        value={item.cantidad}
                        onChange={e => handleItemChange(idx, 'cantidad', e.target.value)}
                        min="0.1"
                        step="0.1"
                        style={{ fontSize: '0.9rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Unidad</label>
                      <select
                        value={item.unidad}
                        onChange={e => handleItemChange(idx, 'unidad', e.target.value)}
                        style={{ fontSize: '0.9rem' }}
                      >
                        {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.75rem' }}>Precio unitario ($)</label>
                      <input
                        type="number"
                        value={item.precio}
                        onChange={e => handleItemChange(idx, 'precio', e.target.value)}
                        placeholder="0"
                        min="0"
                        style={{ fontSize: '0.9rem' }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(idx)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1, padding: '0 4px', alignSelf: 'center', marginTop: 18 }}
                    >×</button>
                  </div>
                  {/* Subtotal */}
                  {item.precio && (
                    <div style={{ marginTop: 8, textAlign: 'right', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      Subtotal: <strong style={{ color: 'var(--text-primary)' }}>{formatCLP((parseFloat(item.precio) || 0) * (parseFloat(item.cantidad) || 1))}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalForm > 0 && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem' }}>
                <span>Total</span>
                <span style={{ color: '#ef4444' }}>{formatCLP(totalForm)}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" className="btn-primary" disabled={saving} style={{ flex: 1, minWidth: 140 }}>
              {saving ? 'Guardando...' : '💾 Guardar compra'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setTab('historial')} style={{ flex: 1, minWidth: 100 }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ─── LISTA DE COMPRAS ─── */}
      {tab === 'lista' && (
        <div>
          {listaItems.length > 0 && (
            <div className="summary-cards" style={{ marginBottom: 20 }}>
              <div className="card">
                <div className="card-icon">📋</div>
                <div className="card-content"><h4>Por comprar</h4><p className="card-amount neutral">{listaPendientes.length}</p></div>
              </div>
              <div className="card">
                <div className="card-icon">✅</div>
                <div className="card-content"><h4>Comprados</h4><p className="card-amount positive">{listaComprados.length}</p></div>
              </div>
              {totalListaEstimado > 0 && (
                <div className="card">
                  <div className="card-icon">💰</div>
                  <div className="card-content"><h4>Estimado pendiente</h4><p className="card-amount negative">{formatCLP(totalListaEstimado)}</p></div>
                </div>
              )}
            </div>
          )}

          {/* Formulario agregar a lista */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 14 }}>Agregar producto a la lista</h3>
            <form onSubmit={handleAddLista}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={listaForm.nombre}
                    onChange={e => setListaForm(f => ({ ...f, nombre: e.target.value }))}
                    placeholder="Leche, Pan, etc."
                    required
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Marca</label>
                  <input
                    type="text"
                    value={listaForm.marca}
                    onChange={e => setListaForm(f => ({ ...f, marca: e.target.value }))}
                    placeholder="Soprole..."
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Cantidad</label>
                  <input
                    type="number"
                    value={listaForm.cantidad}
                    onChange={e => setListaForm(f => ({ ...f, cantidad: e.target.value }))}
                    min="0.1"
                    step="0.1"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Unidad</label>
                  <select
                    value={listaForm.unidad}
                    onChange={e => setListaForm(f => ({ ...f, unidad: e.target.value }))}
                  >
                    {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Precio estimado ($)</label>
                  <input
                    type="number"
                    value={listaForm.precio_estimado}
                    onChange={e => setListaForm(f => ({ ...f, precio_estimado: e.target.value }))}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={saving}>
                + Agregar a la lista
              </button>
            </form>
          </div>

          {listaItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>📋</div>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Lista vacía</p>
              <p style={{ fontSize: '0.9rem' }}>Agrega los productos que necesitas comprar.</p>
            </div>
          ) : (
            <>
              {/* Por comprar */}
              {listaPendientes.length > 0 && (
                <div className="card" style={{ marginBottom: 12 }}>
                  <h3 style={{ fontSize: '0.9rem', margin: '0 0 10px', color: 'var(--text-secondary)' }}>
                    Por comprar ({listaPendientes.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {listaPendientes.map(item => (
                      <div key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        {editingListaId === item.id ? (
                          /* ── Modo edición ── */
                          <div style={{ padding: '10px 0' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 8 }}>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.72rem' }}>Nombre *</label>
                                <input type="text" value={editListaForm.nombre}
                                  onChange={e => setEditListaForm(f => ({ ...f, nombre: e.target.value }))}
                                  style={{ fontSize: '0.88rem' }} autoFocus />
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.72rem' }}>Marca</label>
                                <input type="text" value={editListaForm.marca}
                                  onChange={e => setEditListaForm(f => ({ ...f, marca: e.target.value }))}
                                  style={{ fontSize: '0.88rem' }} placeholder="Opcional" />
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.72rem' }}>Cantidad</label>
                                <input type="number" value={editListaForm.cantidad}
                                  onChange={e => setEditListaForm(f => ({ ...f, cantidad: e.target.value }))}
                                  min="0.1" step="0.1" style={{ fontSize: '0.88rem' }} />
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.72rem' }}>Unidad</label>
                                <select value={editListaForm.unidad}
                                  onChange={e => setEditListaForm(f => ({ ...f, unidad: e.target.value }))}
                                  style={{ fontSize: '0.88rem' }}>
                                  {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.72rem' }}>Precio est. ($)</label>
                                <input type="number" value={editListaForm.precio_estimado}
                                  onChange={e => setEditListaForm(f => ({ ...f, precio_estimado: e.target.value }))}
                                  min="0" placeholder="0" style={{ fontSize: '0.88rem' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn-primary" onClick={() => handleSaveEditLista(item.id)}
                                style={{ fontSize: '0.82rem', padding: '6px 14px' }} disabled={saving}>Guardar</button>
                              <button className="btn-secondary" onClick={() => setEditingListaId(null)}
                                style={{ fontSize: '0.82rem', padding: '6px 14px' }}>Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          /* ── Vista normal ── */
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0' }}>
                            <input type="checkbox" checked={false}
                              onChange={() => handleToggleLista(item)}
                              style={{ width: 20, height: 20, cursor: 'pointer', accentColor: 'var(--primary)', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{item.nombre}</span>
                              {item.marca && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>{item.marca}</span>}
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>{item.cantidad} {item.unidad}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              {item.precio_estimado > 0 && (
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                  ~{formatCLP(item.precio_estimado * item.cantidad)}
                                </span>
                              )}
                              <button onClick={() => handleStartEditLista(item)}
                                title="Editar"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.95rem', padding: '2px 4px', lineHeight: 1 }}>✏️</button>
                              <button onClick={() => handleDeleteLista(item.id)}
                                title="Eliminar"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem', padding: 0, lineHeight: 1 }}>×</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {totalListaEstimado > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem' }}>
                      <span>Total estimado</span>
                      <span style={{ color: '#ef4444' }}>~{formatCLP(totalListaEstimado)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Comprados */}
              {listaComprados.length > 0 && (
                <div className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h3 style={{ fontSize: '0.9rem', margin: 0, color: 'var(--text-muted)' }}>Comprados ({listaComprados.length})</h3>
                    <button className="btn-secondary" onClick={handleClearComprados} style={{ fontSize: '0.78rem', padding: '4px 10px' }}>
                      Limpiar todos
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {listaComprados.map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0', borderBottom: '1px solid var(--border)', opacity: 0.55 }}>
                        <input type="checkbox" checked={true}
                          onChange={() => handleToggleLista(item)}
                          title="Desmarcar como comprado"
                          style={{ width: 20, height: 20, cursor: 'pointer', accentColor: 'var(--primary)', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, textDecoration: 'line-through' }}>
                          <span style={{ fontSize: '0.92rem' }}>{item.nombre}</span>
                          {item.marca && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>{item.marca}</span>}
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>{item.cantidad} {item.unidad}</span>
                        </div>
                        <button onClick={() => handleDeleteLista(item.id)}
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem', padding: 0, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── PREDICCIÓN ─── */}
      {tab === 'prediccion' && (
        <>
          {prediccion.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔮</div>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Aún no hay productos recurrentes</p>
              <p style={{ fontSize: '0.9rem' }}>Cuando un producto aparezca en 2 o más compras de las últimas 4 semanas, lo verás acá.</p>
              <button className="btn-primary" onClick={() => setTab('nueva')} style={{ marginTop: 16 }}>+ Registrar compra</button>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--primary-xlight)', border: '1px solid #bae6fd', borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: '0.9rem', color: '#0369a1' }}>
                ✦ Basado en tus últimas compras. Estimado próxima semana: <strong>{formatCLP(totalPrediccion)}</strong>
              </div>
              <div className="card">
                <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>Lista estimada — próxima semana</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>Productos en 2+ compras de los últimos 28 días</p>
                {prediccion.map(item => (
                  <div key={item.nombre} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: '0.9rem', flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{item.nombre}</span>
                      <span style={{ fontSize: '0.72rem', background: 'var(--bg)', padding: '2px 8px', borderRadius: 99, color: 'var(--text-muted)' }}>
                        {item.veces}× en 4 sem.
                      </span>
                    </div>
                    <span style={{ fontWeight: 700 }}>~{formatCLP(item.precioPromedio)}</span>
                  </div>
                ))}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem' }}>
                  <span>Total estimado</span>
                  <span style={{ color: '#ef4444' }}>{formatCLP(totalPrediccion)}</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Compras;
